import React, { useState } from 'react';
import PageLayout from '../components/PageLayout';
import { ArrowRight, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { newsArticles } from '../data/newsData';
import SEOHead from '../components/SEOHead';

const categories = ['All', 'News', 'Cyber-Security', 'Network', 'Cloud', 'Development'];

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const navigate = useNavigate();

  const popularPosts = newsArticles.slice(0, 3);
  const filtered = activeCategory === 'All'
    ? newsArticles
    : newsArticles.filter(p => p.category === activeCategory);

  return (
    <PageLayout>
      <SEOHead
        title="News & Blog"
        description="Latest updates about security, DevOps, software development, and technology trends from DISION Tech. Stay informed with our expert insights."
        keywords="tech blog, cybersecurity news, DevOps blog, software development news, technology updates, IT news"
        url="/news"
      />
      {/* Page Hero */}
      <div style={{ background: '#F9FAFB', padding: '60px 24px 50px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: '800', color: '#111827', marginBottom: '16px', letterSpacing: '-1px', lineHeight: 1.15 }}>
            All blogs and news
          </h1>
          <p style={{ fontSize: '18px', color: '#6B7280', maxWidth: '500px', lineHeight: 1.7 }}>
            Update now about Security, DevOps and Software Development!
          </p>
        </div>
      </div>

      {/* Popular posts */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '40px', letterSpacing: '-0.5px' }}>Popular posts</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
            {/* Featured post */}
            <div
              className="project-card"
              onClick={() => navigate(`/news/${popularPosts[0].slug}`)}
              style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #E5E7EB', cursor: 'pointer' }}
            >
              <div style={{ lineHeight: 0, position: 'relative' }}>
                <img src={popularPosts[0].image} alt={popularPosts[0].title} style={{ width: '100%', height: '320px', objectFit: 'cover', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=800&q=80'; }} />
                <div style={{ position: 'absolute', top: '16px', left: '16px', backgroundColor: '#0099CC', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{popularPosts[0].category}</div>
              </div>
              <div style={{ padding: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                  <Calendar size={14} color="#9CA3AF" />
                  <span style={{ fontSize: '13px', color: '#9CA3AF' }}>{popularPosts[0].date}</span>
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '12px', lineHeight: 1.4 }}>{popularPosts[0].title}</h3>
                <p style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.7, marginBottom: '16px' }}>{popularPosts[0].excerpt}</p>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#0099CC', fontSize: '14px', fontWeight: '600' }}>Read more <ArrowRight size={14} /></span>
              </div>
            </div>

            {/* Side posts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {popularPosts.slice(1).map((post, i) => (
                <div
                  key={i}
                  className="project-card"
                  onClick={() => navigate(`/news/${post.slug}`)}
                  style={{ display: 'flex', gap: '16px', padding: '16px', borderRadius: '12px', border: '1px solid #E5E7EB', cursor: 'pointer', alignItems: 'flex-start' }}
                >
                  <img src={post.thumbnail} alt={post.title} style={{ width: '90px', height: '70px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=200&q=60'; }} />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <Calendar size={12} color="#9CA3AF" />
                      <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{post.date}</span>
                    </div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', lineHeight: 1.4, margin: 0 }}>{post.title}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Latest posts with filters */}
      <section style={{ padding: '0 24px 80px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', letterSpacing: '-0.5px' }}>Latest posts</h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    padding: '6px 16px', borderRadius: '20px', border: '1px solid',
                    borderColor: activeCategory === cat ? '#0099CC' : '#E5E7EB',
                    background: activeCategory === cat ? '#0099CC' : 'white',
                    color: activeCategory === cat ? 'white' : '#374151',
                    fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#9CA3AF' }}>
              <p style={{ fontSize: '18px' }}>No articles in this category yet.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
              {filtered.map((post, i) => (
                <div
                  key={i}
                  className="project-card"
                  onClick={() => navigate(`/news/${post.slug}`)}
                  style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #E5E7EB', cursor: 'pointer' }}
                >
                  <div style={{ lineHeight: 0, position: 'relative' }}>
                    <img src={post.image} alt={post.title} style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }} onError={e => { e.target.src = 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=400&q=70'; }} />
                    <div style={{ position: 'absolute', top: '12px', left: '12px', backgroundColor: '#0099CC', color: 'white', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{post.category}</div>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                      <Calendar size={13} color="#9CA3AF" />
                      <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{post.date}</span>
                    </div>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '10px', lineHeight: 1.4 }}>{post.title}</h3>
                    <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6, marginBottom: '16px' }}>{post.excerpt}</p>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#0099CC', fontSize: '13px', fontWeight: '600' }}>Read more <ArrowRight size={13} /></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
